import { BakedVertexAnimationManager, Constants, Matrix, RawTexture, Texture, Vector3 } from '@babylonjs/core/pure';
import type { Mesh, Scene } from '@babylonjs/core/pure';
import type { CharacterPartRange } from './actors';
import { RIG_SOLDIER, SOLDIER_PART_BONE } from './anim/rigs';
import { applyPose, bindRigidSkin, buildSkeleton } from './anim/skeletal';
import { clearPose, compileClip, createPose, sampleClip, SOLDIER_CLIP_FIRE, SOLDIER_CLIP_WALK, SOLDIER_CLIP_DEATH, SOLDIER_DEATH_DURATION } from './anim/clips';

const WALK_FRAMES = 48;
const DEATH_START = WALK_FRAMES + 2;
const DEATH_FRAMES = 48;

/** Bake the existing deterministic walk once; the final two frames hold the firing stance. */
export function bakeWalk(scene: Scene, mesh: Mesh, parts: CharacterPartRange[], enemy = false): Float32Array {
  const handle = buildSkeleton(scene, RIG_SOLDIER, 'crowd-bake');
  bindRigidSkin(mesh, handle, parts, RIG_SOLDIER, (name) => SOLDIER_PART_BONE[name]!);
  const stride = (RIG_SOLDIER.bones.length + 1) * 16;
  const matrices = new Float32Array((DEATH_START + (enemy ? 0 : DEATH_FRAMES)) * stride);
  const clip = compileClip(RIG_SOLDIER, enemy ? SOLDIER_CLIP_FIRE : SOLDIER_CLIP_WALK);
  const pose = createPose(RIG_SOLDIER);
  const death = compileClip(RIG_SOLDIER, SOLDIER_CLIP_DEATH);
  for (let frame = 0; frame < matrices.length / stride; frame++) {
    clearPose(pose);
    if (frame < WALK_FRAMES) sampleClip(clip, enemy ? Math.min(1, frame / 18) : frame / (WALK_FRAMES - 1), pose);
    else if (frame >= DEATH_START) sampleClip(death, (frame - DEATH_START) / (DEATH_FRAMES - 1), pose);
    applyPose(handle, pose);
    handle.skeleton.prepare(true);
    matrices.set(handle.skeleton.getTransformMatrices(mesh), frame * stride);
  }
  return matrices;
}

export class BakedCrowd {
  private readonly manager: BakedVertexAnimationManager;
  private readonly parameters: Float32Array;
  private readonly data: Float32Array;
  private time = 0;
  constructor(scene: Scene, private readonly mesh: Mesh, parts: CharacterPartRange[], cap: number, enemy = false) {
    const data = bakeWalk(scene, mesh, parts, enemy);
    this.data = data;
    this.manager = new BakedVertexAnimationManager(scene);
    this.manager.texture = RawTexture.CreateRGBATexture(data, (RIG_SOLDIER.bones.length + 1) * 4, data.length / ((RIG_SOLDIER.bones.length + 1) * 16), scene, false, false, Texture.NEAREST_SAMPLINGMODE, Constants.TEXTURETYPE_FLOAT);
    this.manager.texture.name = 'crowd-walk-matrices';
    mesh.bakedVertexAnimationManager = this.manager;
    this.parameters = new Float32Array(cap * 4);
    mesh.thinInstanceSetBuffer('bakedVertexAnimationSettingsInstanced', this.parameters, 4, false);
    scene.onDisposeObservable.addOnce(() => this.manager.dispose(true));
  }
  slot(index: number, phase: number, hz: number, walking: boolean): void {
    const o = index * 4;
    this.parameters[o] = walking ? 0 : WALK_FRAMES;
    this.parameters[o + 1] = walking ? WALK_FRAMES - 1 : WALK_FRAMES + 1;
    this.parameters[o + 2] = walking ? phase * (WALK_FRAMES - 1) : 0;
    this.parameters[o + 3] = walking ? hz * (WALK_FRAMES - 1) : 1;
  }
  death(index: number, age: number): void {
    const frame = DEATH_START + Math.floor(Math.min(1, Math.max(0, age / SOLDIER_DEATH_DURATION)) * (DEATH_FRAMES - 1));
    this.parameters.set([frame, frame, 0, 0], index * 4);
  }
  muzzle(index: number, point: Vector3): Vector3 {
    const o = index * 4;
    const start = this.parameters[o]!;
    const total = this.parameters[o + 1]! - start + 1;
    const time = this.time * this.parameters[o + 3]! / total;
    const correction = time < 1 ? 0 : 1;
    const count = total - correction;
    const frame = Math.floor(((time - Math.floor(time)) * count + this.parameters[o + 2]!) % count) + start + correction;
    const bone = RIG_SOLDIER.bones.findIndex(b => b.name === 'weapon');
    const offset = frame * (RIG_SOLDIER.bones.length + 1) * 16 + bone * 16;
    return Vector3.TransformCoordinates(point, Matrix.FromArray(this.data, offset));
  }
  update(time: number): void {
    this.time = time;
    this.manager.time = time;
    this.mesh.thinInstanceBufferUpdated('bakedVertexAnimationSettingsInstanced');
  }
}
